import type { DeliverableChannel, DeliverableItem } from './deliverables-card.types'

/** The synthesised "everything" pill. Not a channel value - hence the sentinel. */
export const ALL_CHANNELS = '__all__'

/**
 * The channel pills the deliverables actually carry, plus "All". Derived from the
 * deliverables rather than from a fixed list, so the filter can never offer a channel with
 * nothing behind it - or miss one the taxonomy grew.
 */
export const channelPillsFor = (deliverables: DeliverableItem[], allLabel = 'All'): DeliverableChannel[] => {
	const byCode = new Map<string, string>()
	for (const deliverable of deliverables) {
		for (const channel of deliverable.channels) {
			byCode.set(channel.code, channel.label)
		}
	}
	return [
		{ code: ALL_CHANNELS, label: allLabel },
		...[...byCode].map(([code, label]) => ({ code, label })).sort((a, b) => a.label.localeCompare(b.label)),
	]
}

/**
 * The channel actually in force: the selected one while the deliverables still carry it, "All"
 * otherwise. Re-scoping or filtering re-derives the pills, so a selection made on a wider set
 * can go away under the reader - and a pill nobody can see selected, over an empty grid, reads
 * as a context with no deliverables rather than as a filter that no longer applies.
 *
 * Derived rather than reset: the selection is not thrown away, so a narrowing that brings the
 * channel back honours the pick again - including the frames where a read is still in flight
 * and no deliverable carries any channel yet.
 */
export const channelInForce = (deliverables: DeliverableItem[], channel: string): string =>
	channelPillsFor(deliverables).some((pill) => pill.code === channel) ? channel : ALL_CHANNELS

/**
 * The deliverables visible under `channel`. "All" shows everything, including a deliverable
 * carrying no channel at all - dropping it would report a classification gap as absence.
 */
export const deliverablesOn = (deliverables: DeliverableItem[], channel: string): DeliverableItem[] =>
	channel === ALL_CHANNELS
		? deliverables
		: deliverables.filter((deliverable) => deliverable.channels.some((c) => c.code === channel))
